import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { ShieldCheck, Lock, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AcceptInvite = () => {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!token) { setError('This invitation link is missing a token.'); setLoading(false); return; }
      try {
        const { data } = await axios.get(`${API}/portal-invites/${token}`);
        if (!cancelled) setPreview(data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || 'This invitation link is invalid or expired.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [token]);

  const [alreadyHasAccount, setAlreadyHasAccount] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setAlreadyHasAccount(false);
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/portal-invites/${token}/accept`, { password });
      setDone(true);
      toast.success('Password set — signing you in…');
      setTimeout(() => navigate('/login', { state: { email: preview?.email } }), 1500);
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail || 'Could not accept the invitation.';
      setError(detail);
      // 409 → the customer already has an account. Offer to just sign in.
      if (status === 409) setAlreadyHasAccount(true);
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <div className="w-14 h-14 bg-[#0f392b] rounded-xl flex items-center justify-center shadow-lg mb-4">
            <ShieldCheck className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-2xl font-heading font-bold text-slate-900" data-testid="accept-invite-heading">
            CSG Connect Portal
          </h1>
          <p className="text-sm text-slate-500 mt-1">Set your password to get started</p>
        </div>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-6">
            {loading && (
              <div className="flex items-center justify-center py-8 gap-2 text-slate-500" data-testid="accept-invite-loading">
                <Loader2 className="w-5 h-5 animate-spin" /> Checking invitation…
              </div>
            )}

            {!loading && error && !preview && (
              <Alert variant="destructive" data-testid="accept-invite-error">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {!loading && preview && !done && (
              <>
                <div className="mb-5 pb-5 border-b border-slate-100">
                  <p className="text-xs uppercase tracking-widest text-emerald-700 font-semibold mb-1">Welcome</p>
                  <p className="text-lg font-heading font-semibold text-slate-900" data-testid="accept-invite-customer-name">
                    {preview.customer_name}
                  </p>
                  <p className="text-sm text-slate-500 mt-1">
                    Hi {preview.name || 'there'} — you&apos;re being invited to the CSG portal at{' '}
                    <span className="font-mono text-slate-700">{preview.email}</span>. Choose a password to sign in.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="password" className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                      Choose a password
                    </Label>
                    <div className="relative mt-1">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="password"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Minimum 8 characters"
                        className="pl-9"
                        required
                        minLength={8}
                        autoComplete="new-password"
                        data-testid="accept-invite-password"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="confirm" className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                      Confirm password
                    </Label>
                    <div className="relative mt-1">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="confirm"
                        type="password"
                        value={confirm}
                        onChange={(e) => setConfirm(e.target.value)}
                        placeholder="Re-enter password"
                        className="pl-9"
                        required
                        minLength={8}
                        autoComplete="new-password"
                        data-testid="accept-invite-confirm"
                      />
                    </div>
                  </div>

                  {error && (
                    <Alert variant="destructive" data-testid="accept-invite-form-error">
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  {alreadyHasAccount ? (
                    <div className="space-y-2" data-testid="accept-invite-existing-account">
                      <Button
                        type="button"
                        onClick={() => navigate('/login', { state: { email: preview?.email } })}
                        className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold h-11"
                        data-testid="accept-invite-goto-signin"
                      >
                        Sign in instead →
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => navigate('/forgot-password', { state: { email: preview?.email } })}
                        className="w-full h-11"
                        data-testid="accept-invite-goto-forgot"
                      >
                        I forgot my password
                      </Button>
                    </div>
                  ) : (
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold h-11"
                      data-testid="accept-invite-submit"
                    >
                      {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Set password & continue'}
                    </Button>
                  )}
                </form>
              </>
            )}

            {done && (
              <div className="text-center py-6 space-y-3" data-testid="accept-invite-done">
                <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
                <p className="text-lg font-heading font-semibold text-slate-900">Password set!</p>
                <p className="text-sm text-slate-500">Redirecting you to sign in…</p>
              </div>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-xs text-slate-400 mt-6">
          Need help? Contact your CSG administrator.
        </p>
      </div>
    </div>
  );
};

export default AcceptInvite;
